import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ParkingVehicleType } from '../../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-vehicle-type',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-vehicle-type.component.html',
  styleUrl: './modal-parking-vehicle-type.component.css',
})
export class ModalParkingVehicleTypeComponent implements OnInit {
  @Input() itemId = 0;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingVehicleType;
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.itemId > 0; }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyVehicleType();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void { this.close.emit(); }

  onSave(): void {
    if (!this.formData.name?.trim()) { this.toast.error('กรุณาระบุชื่อ'); return; }
    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) { this.toast.error('ไม่มีสิทธิ์แก้ไข'); return; }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) { this.toast.error('ไม่มีสิทธิ์สร้าง'); return; }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) { this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู'); return; }

    const payload = this.mapper.mapVehicleTypePayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.api.updateParkingVehicleType(this.itemId, payload)
      : this.api.insertParkingVehicleType(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getParkingVehicleTypeById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapVehicleType(res as never);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดล้มเหลว');
      },
    });
  }

  private resolveUserId(): number {
    return Number(localStorage.getItem('userId')) || Number(this.cookieService.get('user_id')) || 0;
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
